import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useState, useCallback } from 'react'

import { CONTAINER_STATUS_META } from 'containers/inventory/settings'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { get } from 'utils/lodash'

const COLLAPSED_COUNT = 3
// Cap the expanded list so long container sets scroll inside the form column instead of stretching the dialog
const LIST_MAX_HEIGHT = 264

interface LocationContainer {
  id: string
  name?: string | null
  serialNumber?: string | null
  status?: string | null
  measure?: { volume?: number | string | null; unit?: string | null } | null
  containerType?: { name?: string | null } | null
}

interface LocationContainersListProps {
  containers: LocationContainer[]
}

// "20 yd Roll-off" — same measure formatting as the container map markers
const getContainerSubtitle = (container: LocationContainer) => {
  const { measure } = container
  const hasVolume = measure?.volume != null && measure.volume !== ''
  const measureLabel = hasVolume ? `${measure?.volume} ${getMeasureUnit(measure?.unit ?? '')}`.trim() : ''
  return [measureLabel, container.containerType?.name].filter(Boolean).join(' ')
}

const getStatusMeta = (container: LocationContainer) => (container.status ? get(CONTAINER_STATUS_META, container.status) : null)

const LocationContainersList = ({ containers }: LocationContainersListProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const visibleContainers = isExpanded ? containers : containers.slice(0, COLLAPSED_COUNT)

  const handleToggleExpanded = useCallback(() => setIsExpanded(prev => !prev), [])

  return (
    <Box minWidth={0} width="100%">
      <Box alignItems="center" display="flex" justifyContent="space-between" mb={1}>
        <Typography variant="h6">{`Containers (${containers.length})`}</Typography>
        {containers.length > COLLAPSED_COUNT && (
          <Button size="small" variant="text" onClick={handleToggleExpanded}>
            {isExpanded ? 'Show less' : 'View all →'}
          </Button>
        )}
      </Box>
      <Stack spacing={1} sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', pr: 0.5 }}>
        {visibleContainers.map((container, index) => {
          const statusMeta = getStatusMeta(container)
          return (
            <Box
              key={container.id}
              alignItems="center"
              border={1}
              borderColor="divider"
              borderRadius={2}
              display="flex"
              flexShrink={0}
              gap={1.5}
              px={1.5}
              py={1}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'primary.main',
                }}
              >
                <Typography sx={{ color: 'common.white' }} variant="subtitle1">
                  {index + 1}
                </Typography>
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography noWrap color="textPrimary" variant="h6">
                  {container.name || container.serialNumber || '—'}
                </Typography>
                {getContainerSubtitle(container) && (
                  <Typography noWrap variant="subtitle2">
                    {getContainerSubtitle(container)}
                  </Typography>
                )}
              </Box>
              {statusMeta && (
                <Chip
                  label={statusMeta.label}
                  size="small"
                  sx={{ flexShrink: 0, color: statusMeta.color, borderColor: statusMeta.color }}
                  variant="outlined"
                />
              )}
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

export default LocationContainersList
